import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Receipt, Paperclip, Upload, FileSpreadsheet, History } from "lucide-react";
import { api, uploadFile, fileUrl } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { money, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import { Combobox } from "@/components/Combobox";
import { ImportExpensesDialog } from "@/components/ImportExpensesDialog";
import { ImportHistoryDialog } from "@/components/ImportHistoryDialog";
import { VendorPicker } from "@/components/VendorPicker";
import { GroupedExpenses } from "@/components/GroupedExpenses";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const CATEGORIES = ["General", "Software / Subscriptions", "Office", "Travel", "Meals", "Contractors", "Equipment", "Equipment Rental", "Marketing"];
const NONE = "__none__";

const emptyForm = () => ({
  vendor: "",
  category: "General",
  account_id: "",
  description: "",
  amount: "",
  gst_amount: "",
  date: todayISO(),
  is_subscription: false,
  receipt_path: "",
  job_id: NONE,
  job_charge: false,
  autoGst: true,
});

export default function Expenses() {
  const { currentId } = useCompany();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });
  const [grouped, setGrouped] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/expenses").then((r) => setRows(r.data));
  useEffect(() => {
    if (!currentId) return;
    load();
    setSelected(new Set());
    api.get("/accounts").then((r) => setAccounts(r.data.filter((a) => a.type === "expense")));
    api.get("/jobs").then((r) => setJobs(r.data));
  }, [currentId]);

  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const visible = rows.filter((r) => (!range.from || r.date >= range.from) && (!range.to || r.date <= range.to));
  const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visible.map((r) => r.id)));
  const setRangeKey = (k, v) => { setRange((r) => ({ ...r, [k]: v })); setSelected(new Set()); };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} expense${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const res = await api.post("/expenses/bulk-delete", { ids });
      toast.success(`Deleted ${res.data.deleted} expense${res.data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error("Could not delete the selected expenses");
    }
  };

  const openNew = () => {
    setForm(emptyForm());
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (x) => {
    setForm({
      vendor: x.vendor,
      category: x.category || "General",
      account_id: x.account_id || "",
      description: x.description || "",
      amount: String(x.amount),
      gst_amount: String(x.gst_amount),
      date: x.date,
      is_subscription: x.is_subscription,
      receipt_path: x.receipt_path || "",
      job_id: x.job_id || NONE,
      job_charge: x.job_charge || false,
      autoGst: false,
    });
    setEditId(x.id);
    setOpen(true);
  };

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!form.vendor.trim()) return toast.error("Vendor is required");
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    const payload = {
      vendor: form.vendor,
      category: form.category,
      account_id: form.account_id || null,
      description: form.description,
      amount,
      gst_amount: form.autoGst ? null : parseFloat(form.gst_amount) || 0,
      date: form.date,
      is_subscription: form.is_subscription,
      receipt_path: form.receipt_path || "",
      job_id: form.job_id === NONE ? null : form.job_id,
      job_charge: form.job_charge,
    };
    try {
      if (editId) await api.put(`/expenses/${editId}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editId ? "Expense updated" : "Expense added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save expense");
    }
  };

  const onReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { path } = await uploadFile(file);
      setForm((f) => ({ ...f, receipt_path: path }));
      toast.success("Receipt attached");
    } catch (err) {
      toast.error("Could not upload receipt");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    load();
  };

  const totalNet = visible.reduce((s, r) => s + (r.job_charge ? 0 : r.amount), 0);
  const renderRow = (x) => (
    <tr key={x.id} className={`border-b border-slate-50 hover:bg-slate-50 ${selected.has(x.id) ? "bg-slate-50" : ""}`}>
      <td className="px-5 py-3">
        <Checkbox data-testid={`select-expense-${x.id}`} checked={selected.has(x.id)} onCheckedChange={() => toggleOne(x.id)} aria-label="Select expense" />
      </td>
      <td className="px-5 py-3 font-medium text-slate-900" style={grouped ? { paddingLeft: 86 } : undefined}>
        {x.vendor}
        {x.is_subscription && (
          <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200">Subscription</Badge>
        )}
        {x.job_charge && (
          <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200" data-testid={`job-charge-badge-${x.id}`}>Job charge</Badge>
        )}
        {x.description && <span className="block text-xs text-slate-400">{x.description}</span>}
        {x.receipt_path && (
          <a href={fileUrl(x.receipt_path)} target="_blank" rel="noreferrer" data-testid={`view-receipt-${x.id}`} className="inline-flex items-center gap-1 text-xs text-blue-600 mt-1 hover:underline">
            <Paperclip className="h-3 w-3" /> View receipt
          </a>
        )}
      </td>
      <td className="px-5 py-3 text-slate-600">{x.category}</td>
      <td className="px-5 py-3 text-slate-600">{fmtDate(x.date)}</td>
      <td className="px-5 py-3 text-right tabular-nums">{money(x.amount)}</td>
      <td className="px-5 py-3 text-right tabular-nums text-blue-600">{x.job_charge ? "—" : money(x.gst_amount)}</td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        <Button variant="ghost" size="icon" data-testid={`edit-expense-${x.id}`} onClick={() => openEdit(x)}>
          <Pencil className="h-4 w-4 text-slate-500" />
        </Button>
        <Button variant="ghost" size="icon" data-testid={`delete-expense-${x.id}`} onClick={() => remove(x.id)}>
          <Trash2 className="h-4 w-4 text-rose-500" />
        </Button>
      </td>
    </tr>
  );
  const vendorOptions = [...new Set(rows.map((r) => r.vendor).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const totalGst = visible.reduce((s, r) => s + (r.job_charge ? 0 : r.gst_amount), 0);
  const autoGstPreview = form.autoGst ? (parseFloat(form.amount) || 0) * 0.05 : parseFloat(form.gst_amount) || 0;

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Expenses</h1>
          <p className="text-slate-500 text-sm mt-1">Capture GST paid (incl. subscriptions) — this feeds your Input Tax Credits.</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button variant="outline" data-testid="bulk-delete-btn" onClick={bulkDelete} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700">
              <Trash2 className="h-4 w-4 mr-2" /> Delete selected ({selected.size})
            </Button>
          )}
          <Button variant="outline" data-testid="import-history-btn" onClick={() => setHistoryOpen(true)} className="rounded-md">
            <History className="h-4 w-4 mr-2" /> Imports
          </Button>
          <Button variant="outline" data-testid="import-expenses-btn" onClick={() => setImportOpen(true)} className="rounded-md">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Import CSV / Excel
          </Button>
          <Button data-testid="add-expense-btn" onClick={openNew} className="rounded-md bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" /> New Expense
          </Button>
        </div>
      </div>

      <ImportExpensesDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        categories={CATEGORIES}
        jobs={jobs}
        onImported={load}
      />
      <ImportHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} onChanged={() => { setSelected(new Set()); load(); }} />

      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="expense-date-filter">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">From</div>
          <Input type="date" data-testid="expense-filter-from" value={range.from} onChange={(e) => setRangeKey("from", e.target.value)} className="rounded-md w-44" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">To</div>
          <Input type="date" data-testid="expense-filter-to" value={range.to} onChange={(e) => setRangeKey("to", e.target.value)} className="rounded-md w-44" />
        </div>
        {(range.from || range.to) && (
          <Button variant="ghost" size="sm" data-testid="expense-filter-clear" onClick={() => setRange({ from: "", to: "" })}>Clear</Button>
        )}
        <VendorPicker rows={visible} selected={selected} setSelected={setSelected} />
        <div className="flex items-center gap-2 pb-2">
          <Switch data-testid="expense-group-toggle" checked={grouped} onCheckedChange={setGrouped} />
          <span className="text-sm text-slate-600">Group by year / month / vendor</span>
        </div>
        <span className="text-sm text-slate-500 pb-2" data-testid="expense-visible-count">Showing {visible.length} of {rows.length}</span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
        <Card className="p-4 rounded-lg border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-400">Total Net Spend</div>
          <div className="font-heading text-2xl font-bold tabular-nums">{money(totalNet)}</div>
        </Card>
        <Card className="p-4 rounded-lg border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wide text-slate-400">GST Paid (ITCs)</div>
          <div className="font-heading text-2xl font-bold tabular-nums text-blue-600">{money(totalGst)}</div>
        </Card>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        {visible.length === 0 ? (
          <div className="p-16 text-center">
            <Receipt className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No expenses recorded yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-5 py-3 w-10">
                  <Checkbox data-testid="select-all-expenses" checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                </th>
                <th className="px-5 py-3 font-medium">Vendor</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium text-right">Net</th>
                <th className="px-5 py-3 font-medium text-right">GST</th>
                <th className="px-5 py-3 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {grouped ? (
                <GroupedExpenses rows={visible} selected={selected} setSelected={setSelected} renderRow={renderRow} />
              ) : (
                [...visible].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map(renderRow)
              )}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Expense" : "New Expense"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor</Label>
                <Combobox
                  testId="expense-vendor-combobox"
                  value={form.vendor}
                  onChange={(v) => setForm({ ...form, vendor: v })}
                  options={vendorOptions}
                  placeholder="Select or type a vendor…"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select" className="mt-1 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Expense Account (posts to ledger)</Label>
              <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                <SelectTrigger data-testid="expense-account-select" className="mt-1 rounded-md">
                  <SelectValue placeholder="General Expenses (default)" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Job (optional)</Label>
              <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                <SelectTrigger data-testid="expense-job-select" className="mt-1 rounded-md">
                  <SelectValue placeholder="No job" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value={NONE}>No job</SelectItem>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.code ? `${j.code} · ` : ""}{j.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Job charge only — not a paid expense</div>
                <div className="text-xs text-slate-500">Excluded from your books &amp; GST, but still billable and counts as a job cost.</div>
              </div>
              <Switch data-testid="expense-job-charge-switch" checked={form.job_charge} onCheckedChange={(v) => setForm({ ...form, job_charge: v })} />
            </div>
            <div>
              <Label>Description</Label>
              <Input data-testid="expense-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-md" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{form.job_charge ? "Amount (charge to job)" : "Amount (net, pre-GST)"}</Label>
                <Input type="number" step="0.01" data-testid="expense-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" />
              </div>
              <div>
                <Label>Date</Label>
                <div className="mt-1">
                  <DatePicker testId="expense-date-picker" value={form.date} onChange={(d) => setForm({ ...form, date: d })} />
                </div>
              </div>
            </div>
            {!form.job_charge && (
              <>
                <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-slate-900">Auto-calc GST (5%)</div>
                    <div className="text-xs text-slate-400">Toggle off to enter exact GST from the receipt</div>
                  </div>
                  <Switch data-testid="expense-autogst-switch" checked={form.autoGst} onCheckedChange={(v) => setForm({ ...form, autoGst: v })} />
                </div>
                {!form.autoGst && (
                  <div>
                    <Label>GST amount</Label>
                    <Input type="number" step="0.01" data-testid="expense-gst-input" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })} className="mt-1 rounded-md text-right tabular-nums" />
                  </div>
                )}
                <div className="text-sm text-slate-500">
                  GST recorded: <span className="font-medium text-slate-900 tabular-nums">{money(autoGstPreview)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div className="text-sm font-medium text-slate-900">This is a subscription / tool</div>
              <Switch data-testid="expense-subscription-switch" checked={form.is_subscription} onCheckedChange={(v) => setForm({ ...form, is_subscription: v })} />
            </div>
            <div className="rounded-md border border-slate-200 px-3 py-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-slate-400" /> Receipt attachment
                </div>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onReceipt} data-testid="expense-receipt-input" />
                <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="expense-receipt-upload-btn" className="rounded-md">
                  <Upload className="h-4 w-4 mr-1" /> {uploading ? "Uploading…" : form.receipt_path ? "Replace" : "Upload"}
                </Button>
              </div>
              {form.receipt_path && (
                <a href={fileUrl(form.receipt_path)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline mt-2 inline-block">
                  View attached receipt
                </a>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-expense-btn" onClick={save} className="rounded-md bg-slate-900 hover:bg-slate-800">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
